import Questions from './Questions';
import { getOptions } from '~/lib/Questions/QuestionOptions';

export function isJump(goto) {
    if (!goto) {
        return false;
    }

    return goto.startsWith('jump-');
}

export function getJumpSectionId(goto) {
    return goto.split('-')[1];
}

export function getUpcomingSectionIds(
    sections,
    currentSectionId,
    visibleSections,
) {
    const upcomingSections = [];
    let seenCurrent = false;
    sections.forEach(s => {
        if (s.section.id === currentSectionId) {
            seenCurrent = true;
            return;
        }

        if (seenCurrent && visibleSections.indexOf(s.section.id) < 0) {
            // We're past the current and it's not visible, then it's upcoming
            upcomingSections.push(s.section.id);
        }
    });

    return upcomingSections;
}

export function firstSection(upcomingSections) {
    return Object.keys(upcomingSections).length > 0
        ? upcomingSections[Object.keys(upcomingSections)[0]]
        : null;
}

export function findSection(id, allSections) {
    return allSections.filter(s => parseInt(s.section.id) === parseInt(id))[0]
        ?.section;
}

export function getAnswer(questionId, allAnswers) {
    if (!allAnswers || !questionId) {
        return null;
    }

    if (`${questionId}`.indexOf('.') > -1) {
        // Subfield!
        const [id, subfield] = questionId.split('.', 2);

        let answerData = allAnswers[id] || {};
        if (Array.isArray(answerData)) {
            answerData = answerData[0];
        }

        if (!answerData) {
            return null;
        }

        return answerData[subfield];
    }

    return allAnswers[questionId];
}

export function getConditionalQuestions(
    questions,
    schema,
    allTypes,
    allBookableResources,
) {
    const conditionQuestions = [];

    (questions || []).forEach(q => {
        const questionSchema = Questions[q.type];
        if (typeof questionSchema.getConditionalFields === 'function') {
            for (const field of questionSchema.getConditionalFields(
                q,
                schema,
                allTypes,
                allBookableResources,
            )) {
                conditionQuestions.push({
                    ...field,
                    id: `${q.id}.${field.id}`,
                });
            }
        } else {
            conditionQuestions.push({
                //question: q,
                id: q.id,
                label: q.label,
                options: getOptions(q, schema),
            });
        }
    });

    return (
        conditionQuestions.filter(o => {
            return o.options.length > 0;
        }) || []
    );
}

export function normalizeAnswer(answer) {
    return ('' + answer).toLowerCase();
}

/**
 *
 * @param fromSection
 * @param allSections
 * @param visibleSections
 * @param answers
 * @returns array of ids
 */
export function nextPossibleSections(
    fromSection,
    allSections,
    visibleSections,
    answers,
) {
    const goto = fromSection.conditional?.goto;
    const upcomingSections = getUpcomingSectionIds(
        allSections,
        fromSection.id,
        visibleSections,
    );
    const nextSection = firstSection(upcomingSections);

    if (goto === 'submit') {
        // Short circuit
        return [];
    } else if (isJump(goto)) {
        return [getJumpSectionId(goto)];
    } else if (goto === 'condition') {
        const possible = [];
        if (!fromSection.conditional?.answerGoto) {
            return nextSection ? [nextSection] : [];
        }
        let answerGoto = Object.values(fromSection.conditional?.answerGoto);

        if (getAnswer(fromSection.conditional?.question, answers)) {
            const answer = getAnswer(
                fromSection.conditional?.question,
                answers,
            );
            answerGoto = [
                fromSection.conditional?.answerGoto[normalizeAnswer(answer)],
            ];
        }

        for (const possibleGoto of answerGoto) {
            if (possibleGoto === 'submit') {
                possible.push('submit');
            } else if (isJump(possibleGoto)) {
                possible.push(getJumpSectionId(possibleGoto));
            } else if (nextSection) {
                possible.push(nextSection);
            }
        }

        // Return unique values
        return [...new Set(possible)];
    } else {
        // Next section, if we have one
        return nextSection ? [nextSection] : [];
    }
}

export function calculatePossiblePaths(
    fromSection,
    allSections,
    visibleSections,
    answers,
) {
    // Current section should always be visible
    if (visibleSections.indexOf(fromSection.id) < 0) {
        visibleSections = [...visibleSections, fromSection.id];
    }

    const nextPossible = nextPossibleSections(
        fromSection,
        allSections,
        visibleSections,
        answers,
    );

    if (nextPossible.length === 0) {
        // This is the last section!
        return [[fromSection.id]];
    }

    // Return an array of all possible paths
    return nextPossible.map(nextId => {
        if (nextId === 'submit') {
            return [fromSection.id];
        } else {
            const nextSection = findSection(nextId, allSections);
            if (!nextSection) {
                console.log(
                    'Failed to find next section',
                    nextId,
                    fromSection,
                    nextPossible,
                    allSections,
                );
                return [fromSection.id];
            }

            if (visibleSections.includes(nextId)) {
                return visibleSections;
            }

            const possiblePaths = calculatePossiblePaths(
                nextSection,
                allSections,
                [...visibleSections, nextId],
                answers,
            );
            let maxPath = possiblePaths[0];
            for (const path of possiblePaths) {
                if (path.length > maxPath.length) {
                    maxPath = path;
                }
            }

            return [fromSection.id, ...maxPath];
        }
    });
}
